import React from "react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { ArrowRight, Instagram, Linkedin, Loader } from "lucide-react";
const NewsletterForm = () => {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="p-4 border border-primary/20 bg-primary/5 rounded-sm">
        <p className="text-primary font-medium">{successMessage}</p>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6">
      <div className="space-y-4">
        <FormField control={control} name="first_name" rules={{
        required: false
      }} render={({
        field
      }) => <FormItem>
              <FormControl>
                <Input placeholder="First Name" {...field} className="border-0 border-b border-primary/30 bg-transparent px-0 py-2 text-base rounded-none shadow-none focus-visible:ring-0 focus-visible:border-primary placeholder:text-muted-foreground/50 h-auto" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <div className="relative">
          <FormField control={control} name="email" rules={{
          required: "Email is required",
          pattern: {
            value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
            message: "Invalid email address"
          }
        }} render={({
          field
        }) => <FormItem>
                <FormControl>
                  <Input type="email" placeholder="Email Address" {...field} className="border-0 border-b border-primary/30 bg-transparent px-0 py-2 text-base rounded-none shadow-none focus-visible:ring-0 focus-visible:border-primary placeholder:text-muted-foreground/50 h-auto pr-10" />
                </FormControl>
                <FormMessage />
              </FormItem>} />
          <Button type="submit" size="icon" variant="ghost" disabled={isSubmitting} className="absolute right-0 bottom-1 text-primary hover:text-primary/80 hover:bg-transparent p-0 h-8 w-8">
            {isSubmitting ? <Loader className="size-4 animate-spin" /> : <ArrowRight className="size-5" />}
            <span className="sr-only">Subscribe</span>
          </Button>
        </div>

        <FormField control={control} name="consent" rules={{
        required: "You must agree to receive updates"
      }} render={({
        field: {
          value,
          onChange,
          ...field
        }
      }) => <FormItem className="flex flex-row items-start space-x-3 space-y-0">
              <FormControl>
                <input type="checkbox" checked={value} onChange={onChange} className="mt-1 size-4 rounded border-primary/30 bg-transparent text-primary focus:ring-primary/50 accent-primary cursor-pointer" {...field} />
              </FormControl>
              <div className="space-y-1 leading-none">
                <FormLabel className="text-xs text-muted-foreground font-normal">
                  I agree to receive event news and updates. We respect your privacy.
                </FormLabel>
                <FormMessage />
              </div>
            </FormItem>} />
      </div>

      {submitError && <p className="text-sm text-destructive mt-2">{submitError}</p>}
    </Form>;
};
export default function Footer() {
  const currentYear = new Date().getFullYear();
  const footerLinks = [{
    label: "Experience",
    href: "/#experience"
  }, {
    label: "Restaurants",
    href: "/#restaurants"
  }, {
    label: "Sponsors",
    href: "/#sponsors"
  }, {
    label: "Impact",
    href: "/#impact"
  }, {
    label: "Gallery",
    href: "/#gallery"
  }, {
    label: "Series Events",
    href: "/#series-events"
  }];
  return <section id="footer" className="dark bg-background text-foreground py-16 lg:py-24 border-t border-primary/10">
      <div className="container mx-auto px-6 lg:px-16">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 lg:gap-24">
          {}
          <div className="flex flex-col items-start space-y-6">
            <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1557866/img/sabor_logo_footer.png" alt="Sabor Denver Logo" className="h-7 w-auto object-contain" />
            <div className="space-y-1">
              <p className="text-primary font-serif italic text-lg">Established 2013</p>
              <p className="text-sm text-muted-foreground">
                &copy; {currentYear} Sabor Denver. All rights reserved.
              </p>
            </div>
          </div>

          {}
          <div className="flex flex-col space-y-6">
            <h3 className="text-lg font-serif font-bold text-foreground sr-only">Navigation</h3>
            <nav className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {footerLinks.map((link, i) => <div key={i} data-index={i}>
                  <Link to={link.href} className="text-primary hover:text-primary/80 transition-colors text-base font-medium inline-block">
                    {link.label}
                  </Link>
                </div>)}
            </nav>
          </div>

          {}
          <div className="flex flex-col space-y-8">
            <div className="space-y-4">
              <h3 className="text-2xl font-serif font-bold text-foreground">Stay Informed</h3>
              <FormProvider formId="newsletter_signup_home" sectionName="footer" defaultValues={{
              first_name: "",
              email: "",
              consent: false
            }} validationRules={{
              email: {
                required: true,
                pattern: "email"
              },
              consent: {
                required: true
              }
            }} submitText="Subscribe" successMessage="Thank you for subscribing to our updates.">
                <NewsletterForm />
              </FormProvider>
            </div>

            <div className="flex items-center gap-6 pt-4">
              <Link to="https://linkedin.com" newTab className="text-primary hover:text-primary/80 transition-colors" aria-label="LinkedIn">
                <Linkedin className="size-6" />
              </Link>
              <Link to="https://instagram.com" newTab className="text-primary hover:text-primary/80 transition-colors" aria-label="Instagram">
                <Instagram className="size-6" />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>;
}